import {
  Avatar,
  Box,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  Paper,
  Typography
} from '@mui/material';
import { useAtomValue, useSetAtom } from 'jotai';
import { loadable } from 'jotai/utils';
import * as React from 'react';
import { useUpdated } from '../hooks/useUpdated';
import { token } from '../params';
import {
  type IParentalControl,
  parentalControlState,
  profileState
} from '../recoil';
import { RESET_SYMBOL } from '../recoil/atomRewind';
import { request } from '../utils/request';
import { useConfirm } from './ConfirmProvider';
import { Fit } from './Fit';
import { noticeAtom } from './NoticeManager';

const styles = {
  profile: {
    backgroundColor: 'background.paper',
    display: 'flex',
    alignItems: 'center',
    padding: 4,
    paddingTop: 8
  },
  container: {
    mx: 'auto',
    my: 2,
    padding: 2,
    maxWidth: 700
  },
  heading: {
    marginBottom: 2
  },
  label: {
    display: 'block',
    marginTop: 2
  }
} as const;

export function Preference() {
  return (
    <div>
      <Box sx={styles.profile}>
        <Profile />
      </Box>
      <Paper elevation={0} sx={styles.container}>
        <Typography variant="h5" sx={styles.heading}>
          みまもり設定の変更
        </Typography>
        <ToggleItem
          name="enablePublish"
          label="作品の公開を許可する"
          beFalseMessage="この設定を適用すると、作品の公開が出来なくなります。作品の公開が出来ないと、プログラミングを学ぶ意欲を損なう可能性があります。本当に設定を変更しますか？"
          falseMessage="作品の公開ができない設定になっています"
          sx={styles.label}
        />
        <ToggleItem
          name="enableReceive"
          label="自分の作品に対するコメントを受け取る"
          falseMessage="コメントを受け取れない設定になっています"
          sx={styles.label}
        />
        <ToggleItem
          name="enableSend"
          label="他の人の作品に対してコメントを送れる"
          falseMessage="コメントを送れない設定になっています"
          sx={styles.label}
        />
      </Paper>
      <Box sx={styles.container}>
        <Typography variant="body1" color="text.secondary">
          上記のチェックボックスをクリックすると、みまもり設定を変更できます
        </Typography>
      </Box>
    </div>
  );
}

const componentStyles = {
  avatar: {
    width: 80,
    height: 80,
    marginRight: 2
  },
  progress: {
    margin: '9px'
  },
  falseMessage: {
    marginLeft: 4
  }
} as const;

const profileLoadableAtom = loadable(profileState);

function Profile() {
  const profileLoadable = useAtomValue(profileLoadableAtom);

  if (profileLoadable.state === 'hasError') {
    throw profileLoadable.error;
  }

  return profileLoadable.state === 'loading' ? (
    <Fit>
      <CircularProgress />
    </Fit>
  ) : (
    <>
      <Avatar
        src={profileLoadable.data.iconUrl}
        alt="icon"
        sx={componentStyles.avatar}
      />
      <Typography variant="h6">{profileLoadable.data.displayName}</Typography>
    </>
  );
}

interface ToggleItemProps {
  name: keyof IParentalControl;
  label: string;
  /**
   * true -> false に変更するときに表示するダイアログ
   */
  beFalseMessage?: string;
  /**
   * false になっているときに下に出す文字
   */
  falseMessage: string;
  sx?: object;
}

const parentalControlLoadableAtom = loadable(parentalControlState);

function ToggleItem(props: ToggleItemProps) {
  // 実際の値 (サーバの値)
  const parentalControlLoadable = useAtomValue(parentalControlLoadableAtom);
  const defaultValue =
    parentalControlLoadable.state === 'hasData'
      ? parentalControlLoadable.data[props.name]
      : undefined;

  // 表示する値
  const [value, setValue] = React.useState(defaultValue);
  useUpdated(() => {
    if (defaultValue !== undefined) {
      setValue(defaultValue); // サーバの値が変化したら同期する
    }
  }, [defaultValue]);

  const setNotice = useSetAtom(noticeAtom);
  const setParentalControl = useSetAtom(parentalControlState);
  const confirm = useConfirm();
  const toggle = React.useCallback(async () => {
    // 確認のダイアログを表示する
    if (value && props.beFalseMessage) {
      const answer = await confirm(
        props.beFalseMessage,
        'このままにする',
        '不許可にする'
      );
      if (answer) return;
    }

    const nextValue = !value;
    setValue(nextValue);
    try {
      await request('/api/parentalControls', 'POST', {
        token: token,
        key: props.name,
        value: nextValue
      });

      setParentalControl(RESET_SYMBOL); // もう一度取得させる
      setNotice({
        severity: 'success',
        children: '設定を変更できました！'
      });
    } catch (error) {
      console.error(error);
      setNotice({
        severity: 'error',
        children:
          '設定を変更できませんでした。申し訳ありませんが、「みまもり設定」のメールをもう一度送信してください。'
      });
    }
  }, [value]);

  if (parentalControlLoadable.state === 'hasError') {
    throw parentalControlLoadable.error;
  }

  return (
    <>
      <FormControlLabel
        control={
          parentalControlLoadable.state !== 'loading' ? (
            <Checkbox
              checked={Boolean(value)}
              onChange={toggle}
              disabled={value !== defaultValue}
              color="primary"
            />
          ) : (
            <CircularProgress
              color="primary"
              size={24}
              sx={componentStyles.progress}
            />
          )
        }
        label={props.label}
        sx={props.sx}
      />
      {parentalControlLoadable.state === 'loading' || value ? null : (
        <Typography
          variant="body1"
          color="text.secondary"
          sx={componentStyles.falseMessage}
        >
          {props.falseMessage}
        </Typography>
      )}
    </>
  );
}
